/**
 * **The booth door** (`/booth`, overnight 6 item 0). A check-in station types this address and
 * always lands on one screen with the mark — never the password sign-in, never a blank, never a
 * director's screen.
 *
 * A station is a device, not a person. **`/booth` is its door** (James, Oct 6): an enrolled
 * station opens straight to the booth and stays open across restarts (its session lasts as long
 * as a device's does); a station that is not enrolled shows one screen — "Enroll this station" —
 * and takes the six-digit code a director reads off Festival Admin → Devices. After the code it
 * is enrolled and never asks again. The long token address (`/booth/<token>`) still works and
 * is never shown to a volunteer. The installed booth app opens at `/booth`.
 *
 * An open station is the booth itself (`BoothStation.tsx`, `Booth.dc.html`'s operator screen):
 * find, check in, walk-up, check out, the day's count. The not-set-up and the code screens are
 * not drawn on the canvas: defect #167.
 */
import { useState, type ReactNode } from 'react';
import { api, sentence } from '../../shell/api.js';
import type { Me } from '../../shell/session.js';
import { BoothStation } from './BoothStation.js';
import { MidwayStation } from './MidwayStation.js';
import { LoginFrame } from './Login.js';

export interface BoothProps {
  /** The station's token, from its own address (`/booth/<token>`); null at plain `/booth`. */
  token: string | null;
  me: Me | null;
  onOpened: () => Promise<void>;
}

export function BoothDoor({ token, me, onOpened }: BoothProps): ReactNode {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const device = me?.signedIn && me.kind === 'device' ? me.device : undefined;
  const person = me?.signedIn && me.kind !== 'device' ? (me.name ?? me.email ?? 'somebody') : null;

  const open = (): void => {
    setBusy(true);
    setRefusal(null);
    (token
      ? api('POST', '/auth/device', { deviceToken: token, pin: code.trim() })
      : api('POST', '/auth/device/code', { code: code.trim() })
    )
      .then(async () => {
        setCode('');
        await onOpened();
      })
      .catch((e: unknown) => {
        setRefusal(sentence(e));
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const close = (): void => {
    setBusy(true);
    void api('POST', '/auth/sign-out')
      .catch(() => undefined)
      .finally(() => {
        setBusy(false);
        void onOpened();
      });
  };

  // An open station: the booth itself (`BoothStation`, the seventh overnight) — or, for a door
  // device at the midway, the bracelet window (ON21).
  if (device?.kind === 'door') return <MidwayStation onClose={close} label={device.label} />;
  if (device) return <BoothStation onClose={close} label={device.label} />;

  // The station's own address: one box, the code.
  return (
    <LoginFrame>
      <form
        className="login__card2"
        data-screen="booth"
        data-booth="code"
        onSubmit={(e) => {
          e.preventDefault();
          open();
        }}
      >
        <div className="login__eyebrow">CHECK-IN STATION</div>
        <h2>{token ? 'Open this station' : 'Enroll this station'}</h2>
        <p data-testid="booth-says">
          {token
            ? 'A director or the booth chair types the station’s six-digit code.'
            : 'Type the six-digit code a director reads off Festival Admin → Devices — a check-in station’s or a door device’s. After it, this device opens by itself — it never asks again.'}
        </p>
        <div className="login__field">
          <label className="login__label" htmlFor="booth-code">
            Station code
          </label>
          <input
            className="login__input"
            id="booth-code"
            type="password"
            inputMode="numeric"
            autoComplete="off"
            required
            minLength={6}
            maxLength={token ? 12 : 6}
            value={code}
            onChange={(e) => {
              setCode(e.target.value);
            }}
          />
        </div>
        {person ? (
          <p className="login__fine">Opening the station signs {person} out of this browser.</p>
        ) : null}
        <p className="login__fine">
          {token
            ? 'Three wrong codes lock this station for fifteen minutes. A volunteer chair can unlock it from Festival Admin → Devices.'
            : 'A code works for one station. A director gives a station a new code from Festival Admin → Devices.'}
        </p>
        {refusal ? (
          <p className="login__refusal" role="alert">
            {refusal}
          </p>
        ) : null}
        <button type="submit" className="login__go" disabled={busy}>
          {token ? 'Open the station' : 'Enroll this station'}
        </button>
      </form>
    </LoginFrame>
  );
}
